import * as React from 'react'
import { supabase } from './supabase'
import { Button, Dialog } from './ui'
import { dateLabel } from './operationsModel'

export type AssignmentBooking = {
  id: string
  booking_reference: string
  customer_name: string | null
  customer_email: string | null
  delivery_date: string
  operational_delivery_date?: string | null
  operational_collection_date?: string | null
  collection_date: string
  quantity: number
  updated_at: string
  payment_state: string
  assignment_mode: 'auto' | 'manual'
  products: { name: string; sku: string | null; product_types?: { assignment_mode: 'auto' | 'manual' } | null }
}

type Unit = {
  id: string
  asset_code: string
  manual_only: boolean
  product_id: string
  product_name: string
  sku: string | null
  mismatched: boolean
  available: boolean
}

const message = (error: unknown) => error && typeof error === 'object' && 'message' in error
  ? String(error.message)
  : 'The action could not be completed.'

function assignmentError(error: unknown) {
  const code = message(error)
  if (code.includes('UNIT_UNAVAILABLE')) return 'One of these units is no longer available. Refresh the list and choose another unit.'
  if (code.includes('BOOKING_NOT_UNASSIGNED') || code.includes('BOOKING_NOT_CONFIRMED') || code.includes('BOOKING_CHANGED_REFRESH')) return 'This booking has changed. Close this popup and refresh the bookings list.'
  if (code.includes('PAID_BOOKING_REQUIRED')) return 'This booking can no longer be assigned. Check its payment status.'
  if (code.includes('UNITS_MUST_MATCH_BOOKED_CLASS')) return 'One of these units is no longer active or belongs to a different device class. Refresh the list and choose another unit.'
  return code
}

export function BookingAssignmentDialog({ booking, onClose, onSaved, change = false, initialIds = [] }: {
  booking: AssignmentBooking
  change?: boolean
  initialIds?: string[]
  onClose: () => void
  onSaved: () => Promise<void>
}) {
  const refunded = booking.payment_state === 'refunded'
  const manualAssignment = booking.assignment_mode === 'manual' || booking.products.product_types?.assignment_mode === 'manual'
  const [units, setUnits] = React.useState<Unit[]>([])
  const [ids, setIds] = React.useState<string[]>(initialIds)
  const [loading, setLoading] = React.useState(!refunded)
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState('')
  const generation = React.useRef(0)

  const loadUnits = React.useCallback(async () => {
    const request = ++generation.current
    setLoading(true)
    setError('')
    try {
      const { data, error } = await supabase.rpc('admin_booking_assignment_units', { p_booking_id: booking.id })
      if (error) throw error
      if (request !== generation.current) return
      const availableUnits = (data ?? []) as Unit[]
      setUnits(availableUnits)
      setIds(current => current.filter(id => availableUnits.some(unit => unit.id === id && unit.available)))
    } catch (error) {
      if (request === generation.current) setError(message(error))
    } finally {
      if (request === generation.current) setLoading(false)
    }
  }, [booking.id])

  React.useEffect(() => {
    if (!refunded) void loadUnits()
    return () => { generation.current++ }
  }, [loadUnits, refunded])

  const canSave = !busy && !loading && (refunded || ids.length === booking.quantity)
  const save = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!canSave) return
    setBusy(true)
    setError('')
    try {
      const { error } = refunded
        ? await supabase.rpc('admin_resolve_manual_review', {
          p_booking_id: booking.id,
          p_action: 'cancel',
          p_note: 'Unassigned booking cancelled after full refund.',
        })
        : await supabase.rpc(change ? 'admin_change_booking_units' : 'admin_assign_booking_units', {
          p_booking_id: booking.id,
          p_unit_ids: ids,
          p_expected_updated_at: booking.updated_at,
        })
      if (error) throw error
      await onSaved()
    } catch (error) {
      setError(assignmentError(error))
    } finally {
      setBusy(false)
    }
  }
  const substitutions = units.filter(unit => ids.includes(unit.id) && unit.mismatched)

  return <Dialog isOpen onOpenChange={open => { if (!open && !busy) onClose() }} purpose="form" width="min(700px, 96vw)" maxHeight="92dvh">
    <form className="ops-dialog" onSubmit={event => void save(event)}>
      <h2>{refunded ? 'Cancel refunded booking' : change ? 'Change equipment' : 'Assign equipment'} · {booking.booking_reference}</h2>
      <p>{booking.customer_name || booking.customer_email} · {booking.products.name} · {booking.quantity} unit{booking.quantity === 1 ? '' : 's'}</p>
      <p>{dateLabel(booking.operational_delivery_date || booking.delivery_date)} → {dateLabel(booking.operational_collection_date || booking.collection_date)}</p>
      {refunded
        ? <p>The payment has been fully refunded. Cancel this booking to release its delivery and collection reservations.</p>
        : <>
          <p>Choose {booking.quantity} unit{booking.quantity === 1 ? '' : 's'} from the same device class. {manualAssignment
            ? 'Availability covers the booking through its collection date, with no turnaround buffer. Staff-assigned hires can share a collection and delivery date; arrange collection before the next delivery.'
            : 'Availability includes turnaround time.'}</p>
          <Button type="button" disabled={busy || loading} onClick={() => void loadUnits()}>Refresh units</Button>
          {loading ? <p>Loading units…</p> : <fieldset className="ops-unit-picker" disabled={busy}>
            <legend>Equipment · {ids.length} of {booking.quantity} selected</legend>
            {units.map(unit => {
              const checked = ids.includes(unit.id)
              return <label key={unit.id}>
                <input type="checkbox" checked={checked}
                  disabled={!unit.available || (!checked && ids.length >= booking.quantity)}
                  onChange={event => setIds(current => event.target.checked ? [...current, unit.id] : current.filter(id => id !== unit.id))} />
                <span>
                  <strong>{unit.asset_code}</strong>
                  <small>{unit.product_name}{unit.sku ? ` · ${unit.sku}` : ''}</small>
                  {unit.mismatched && <small>Different model to the original order</small>}
                  {unit.manual_only && <small>Manual-only</small>}
                  {!unit.available && <small className="ops-conflict">Unavailable for these dates</small>}
                </span>
              </label>
            })}
            {!units.length && <p>No active units in this device class. Add inventory before assigning this booking.</p>}
          </fieldset>}
        </>}
      {substitutions.length > 0 && <p className="message warning" role="alert">
        <strong>Model substitution:</strong> Originally ordered {booking.products.name}. You selected {substitutions.map(unit => `${unit.asset_code} (${unit.product_name})`).join(', ')}. You can still assign these units; the original order and assigned equipment will both be recorded.
      </p>}
      {error && <p className="message error" role="alert">{error}</p>}
      <div className="ops-actions">
        <Button type="button" disabled={busy} onClick={onClose}>Close</Button>
        <Button className={refunded ? 'danger' : 'primary'} disabled={!canSave}>
          {busy ? 'Saving…' : refunded ? 'Cancel after refund' : change ? 'Save unit changes' : 'Assign units'}
        </Button>
      </div>
    </form>
  </Dialog>
}
